import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminFormSection from '../components/AdminFormSection';
import AdminSubpage from '../components/AdminSubpage';
import ProfileImageField from '../components/ProfileImageField';
import '../components/People.css';
import { uploadAPI, usersAPI } from '../services/api';

const emptyForm = {
  email: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  profile_image_url: '',
  is_admin: false,
  password: '',
};

const UserFormPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(userId);
  const [formData, setFormData] = useState(emptyForm);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) {
      setFormData(emptyForm);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await usersAPI.getById(userId);
        const user = response.data;
        if (!cancelled && user) {
          setFormData({
            email: user.email || '',
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            phone_number: user.phone_number || '',
            profile_image_url: user.profile_image_url || '',
            is_admin: user.is_admin || false,
            password: '',
          });
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, isEdit]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data.image_url;
      setFormData((prev) => ({ ...prev, profile_image_url: imageUrl }));
      toast.success('Image uploaded');
    } catch (error) {
      let errorMessage = 'Error uploading image: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEdit) {
        const updateData = { ...formData };
        if (!updateData.password || updateData.password.trim() === '') {
          delete updateData.password;
        }
        if (updateData.profile_image_url === '') {
          updateData.profile_image_url = null;
        }
        updateData.is_admin = updateData.is_admin || false;
        delete updateData.is_influencer;

        await usersAPI.update(userId, updateData);
        toast.success('User updated');
      } else {
        const createData = { ...formData };
        if (!createData.password || createData.password.trim() === '') {
          toast.error('Password is required for new users');
          return;
        }
        if (createData.profile_image_url === '') {
          createData.profile_image_url = null;
        }
        createData.is_admin = createData.is_admin || false;

        await usersAPI.create(createData);
        toast.success('User created');
      }
      navigate('/users');
    } catch (error) {
      let errorMessage = isEdit ? 'Error updating user: ' : 'Error creating user: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      toast.error(errorMessage);
    }
  };

  const title = isEdit ? 'Edit team user' : 'Add team user';

  return (
    <AdminSubpage title={title} backTo="/users">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-form-page">
          <form className="admin-form-stack" onSubmit={handleSubmit}>
            <AdminFormSection title="Account" hint="Login credentials and access level.">
              <div className="form-group">
                <label htmlFor="user-email">Email *</label>
                <input
                  id="user-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="user-password">
                  {isEdit ? 'New password' : 'Password *'}
                </label>
                <input
                  id="user-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={isEdit ? 'Leave blank to keep current' : 'Minimum 6 characters'}
                  minLength={6}
                  required={!isEdit}
                />
                <small className="field-hint">
                  {isEdit
                    ? 'Only enter a password if you want to change it.'
                    : 'Required for new accounts.'}
                </small>
              </div>
              <label className="admin-form-check">
                <input
                  type="checkbox"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                />
                <span>
                  <strong>Administrator</strong>
                  <span>Can access the full admin panel and settings.</span>
                </span>
              </label>
            </AdminFormSection>

            <AdminFormSection title="Profile" hint="Name and contact details shown in the app.">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="user-first-name">First name</label>
                  <input
                    id="user-first-name"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="user-last-name">Last name</label>
                  <input
                    id="user-last-name"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="user-phone">Phone number</label>
                <input
                  id="user-phone"
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="+971501234567"
                />
              </div>
              <ProfileImageField
                value={formData.profile_image_url}
                onChange={(url) => setFormData({ ...formData, profile_image_url: url })}
                onUpload={handleImageUpload}
                uploading={uploadingImage}
                person={formData}
              />
            </AdminFormSection>

            <div className="admin-form-footer">
              <button type="submit" className="btn btn-primary">
                {isEdit ? 'Save changes' : 'Create user'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/users')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default UserFormPage;
